import React, { useState, useEffect, useCallback } from "react";
import useApiEp from "../hooks/useApiEp";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Download,
  Search,
  FilterList,
  Clear,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import CustomCircularP from "../widgets/customCircularP";
import debounce from "lodash/debounce";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import { toast } from "react-toastify";
const PAGE_SIZE = 10;
const API_BASE = "admin";

const Stocks = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Filter states
  const [searchInput, setSearchInput] = useState(""); // Immediate input value
  const [searchLotNo, setSearchLotNo] = useState(""); // Debounced search value for API
  const [filters, setFilters] = useState({
    seed_type: "",
    lot_type: "",
    cold_storage: "",
    user_id: "",
    egg_laying_date: "",
    cold_storage_date: "",
    start_release_date: "",
    end_release_date: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  // Fetch unique values for dropdowns
  const { data: coldStorageData } = useApiEp("admin/get-unique-cold-storage");
  const { data: userIdData } = useApiEp("admin/get-unique-user-id");
  const uniqueColdStorages = coldStorageData || [];
  const uniqueUserIds = userIdData || [];

  // Build query string
  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage);

    if (searchLotNo.trim()) {
      params.append("lot_no", searchLotNo.trim());
    }

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    return params.toString();
  };

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `admin/get-stocks?${buildQueryString()}`
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchLotNo, appliedFilters]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = useCallback(
    debounce((value) => {
      setSearchLotNo(value);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      handleSearchChange.cancel();
    };
  }, [handleSearchChange]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      seed_type: "",
      lot_type: "",
      cold_storage: "",
      user_id: "",
      egg_laying_date: "",
      cold_storage_date: "",
      start_release_date: "",
      end_release_date: "",
    };
    setFilters(clearedFilters);
    setAppliedFilters({});
    setSearchInput("");
    setSearchLotNo("");
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchLotNo.trim()) count++;
    Object.values(appliedFilters).forEach((value) => {
      if (value) count++;
    });
    return count;
  };

  const handleView = (stock) => {
    setSelectedStock(stock);
    setViewModalOpen(true);
  };

  const handleEditOpen = (stock) => {
    setSelectedStock(stock);
    setEditForm({ ...stock });
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedStock(null);
    setEditForm({});
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditSave = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}admin/update-stock/${selectedStock._id}`,
        {
          method: "PUT",
          ...getHeader(),
          body: JSON.stringify(editForm),
        }
      );
      if (response.ok) {
        //refetch();
        //handleEditClose();
        toast.success("Saved successfully!");
      } else {
        alert("Failed to update stock");
      }
    } catch (err) {
      alert("Error updating stock");
    }
  };

  const handleDeleteOpen = (stock) => {
    setSelectedStock(stock);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `/api/${API_BASE}/delete-stock/${selectedStock._id}`,
        {
          method: "DELETE",
        }
      );
      if (response.ok) {
        refetch();
        setDeleteConfirmOpen(false);
        setSelectedStock(null);
      } else {
        alert("Failed to delete stock");
      }
    } catch (err) {
      alert("Error deleting stock");
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Build query string for export
      const params = new URLSearchParams();
      if (searchLotNo.trim()) {
        params.append("lot_no", searchLotNo.trim());
      }
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${API_ENDPOINT}admin/get-all-stocks${queryString}`,
        {
          method: "GET",
          ...getHeader(),
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const allStocks = await res.json();

      // Check if data exists
      if (!allStocks || allStocks.length === 0) {
        alert("No data to export");
        return;
      }

      const ws = XLSX.utils.json_to_sheet(allStocks);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stocks");
      XLSX.writeFile(wb, "stocks_inventory.xlsx");
    } catch (err) {
      console.error("Export error:", err);
      alert("Error exporting data: " + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">{error?.message || "An error occurred"}</Alert>
      </Box>
    );
  }

  const stocks = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Box p={3}>
      <CustomCircularP show={isLoading} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">
          Stocks Inventory ({data ? data?.totalItems : "..."})
        </Typography>
        <Tooltip title="Export to Excel">
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? <CircularProgress size={20} /> : "Export All"}
          </Button>
        </Tooltip>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Lot Number..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                handleSearchChange(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchLotNo("");
                      }}
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                fullWidth
              >
                Filters{" "}
                {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
              </Button>
              {getActiveFilterCount() > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Filter Panel */}
        {showFilters && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Seed Type</InputLabel>
                  <Select
                    value={filters.seed_type}
                    onChange={(e) =>
                      handleFilterChange("seed_type", e.target.value)
                    }
                    label="Seed Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="BV">BV</MenuItem>
                    <MenuItem value="CB">CB</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Lot Type</InputLabel>
                  <Select
                    value={filters.lot_type}
                    onChange={(e) =>
                      handleFilterChange("lot_type", e.target.value)
                    }
                    label="Lot Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="FC1">FC1</MenuItem>
                    <MenuItem value="FC2">FC2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Cold Storage</InputLabel>
                  <Select
                    value={filters.cold_storage}
                    onChange={(e) =>
                      handleFilterChange("cold_storage", e.target.value)
                    }
                    label="Cold Storage"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniqueColdStorages.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>User ID</InputLabel>
                  <Select
                    value={filters.user_id}
                    onChange={(e) =>
                      handleFilterChange("user_id", e.target.value)
                    }
                    label="User ID"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniqueUserIds.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Egg Laying Date"
                  type="date"
                  value={filters.egg_laying_date}
                  onChange={(e) =>
                    handleFilterChange("egg_laying_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Cold Storage Date"
                  type="date"
                  value={filters.cold_storage_date}
                  onChange={(e) =>
                    handleFilterChange("cold_storage_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Start Release Date"
                  type="date"
                  value={filters.start_release_date}
                  onChange={(e) =>
                    handleFilterChange("start_release_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="End Release Date"
                  type="date"
                  value={filters.end_release_date}
                  onChange={(e) =>
                    handleFilterChange("end_release_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button onClick={() => setShowFilters(false)}>Cancel</Button>
                  <Button variant="contained" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Active Filters Display */}
        {getActiveFilterCount() > 0 && !showFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchLotNo && (
              <Chip
                label={`Lot No: ${searchLotNo}`}
                onDelete={() => {
                  setSearchInput("");
                  setSearchLotNo("");
                }}
                size="small"
              />
            )}
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (value) {
                return (
                  <Chip
                    key={key}
                    label={`${key.replace(/_/g, " ")}: ${value}`}
                    onDelete={() => {
                      const newFilters = { ...appliedFilters };
                      delete newFilters[key];
                      setAppliedFilters(newFilters);
                      setFilters((prev) => ({ ...prev, [key]: "" }));
                    }}
                    size="small"
                  />
                );
              }
              return null;
            })}
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lot Type</TableCell>
              <TableCell>Cocoon Source</TableCell>
              <TableCell>Seed Type</TableCell>
              <TableCell>Lot No</TableCell>
              <TableCell>Lot Quantity</TableCell>
              <TableCell>Cold Storage</TableCell>
              <TableCell>Available Stock</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stocks.map((stock) => (
              <TableRow key={stock._id}>
                <TableCell>{stock.lot_type}</TableCell>
                <TableCell>{stock.cocoon_source}</TableCell>
                <TableCell>{stock.seed_type}</TableCell>
                <TableCell>{stock.lot_no}</TableCell>
                <TableCell>{stock.lot_quantity.toLocaleString()}</TableCell>
                <TableCell>{stock.cold_storage}</TableCell>
                <TableCell>{stock.available_stock.toLocaleString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleView(stock)} size="small">
                    <Visibility fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleEditOpen(stock)}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteOpen(stock)}
                    size="small"
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
      {stocks.length === 0 && (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>No stocks found.</Typography>
        </Box>
      )}

      {/* View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Stock Details</DialogTitle>
        <DialogContent>
          {selectedStock && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography>
                  <strong>Lot Type:</strong> {selectedStock.lot_type}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Cocoon Source:</strong> {selectedStock.cocoon_source}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Seed Type:</strong> {selectedStock.seed_type}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Lot No:</strong> {selectedStock.lot_no}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Lot Quantity:</strong>{" "}
                  {selectedStock.lot_quantity.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Lot Cycle:</strong> {selectedStock.lot_cycle}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Cold Storage:</strong> {selectedStock.cold_storage}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Egg Laying Date:</strong>{" "}
                  {formatDate(selectedStock.egg_laying_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Cold Storage Date:</strong>{" "}
                  {formatDate(selectedStock.cold_storage_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Start Release Date:</strong>{" "}
                  {formatDate(selectedStock.start_release_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>End Release Date:</strong>{" "}
                  {formatDate(selectedStock.end_release_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Price:</strong> ₹
                  {selectedStock.price.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Available Stock:</strong>{" "}
                  {selectedStock.available_stock.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  <strong>User ID:</strong> {selectedStock.user_id}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Stock</DialogTitle>
        <DialogContent>
          {selectedStock && (
            <Box component="form" sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Lot Type"
                    name="lot_type"
                    value={editForm.lot_type || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cocoon Source"
                    name="cocoon_source"
                    value={editForm.cocoon_source || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Seed Type"
                    name="seed_type"
                    value={editForm.seed_type || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Lot No"
                    name="lot_no"
                    value={editForm.lot_no || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Lot Quantity"
                    name="lot_quantity"
                    type="number"
                    value={editForm.lot_quantity || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Lot Cycle"
                    name="lot_cycle"
                    value={editForm.lot_cycle || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cold Storage"
                    name="cold_storage"
                    value={editForm.cold_storage || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    type="number"
                    value={editForm.price || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Available Stock"
                    name="available_stock"
                    type="number"
                    value={editForm.available_stock || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="User ID"
                    name="user_id"
                    value={editForm.user_id || ""}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="Financial Year"
                    name="financial_year"
                    value={editForm.financial_year || ""}
                    onChange={handleEditChange}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value=""></option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2025-2026">2025-2026</option>
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this stock item?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stocks;
